import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Card = ({ title, children }) => (
  <section className="bg-white border border-gray-200 rounded-lg p-5 sm:p-6"><h2 className="font-display text-xl font-bold tracking-tight mb-4">{title}</h2>{children}</section>
);
const Field = ({ label, hint, children }) => (<div className="space-y-1.5"><Label>{label}</Label>{children}{hint && <p className="text-xs text-gray-500">{hint}</p>}</div>);

const GeneralSettings = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const num = (k) => (e) => setS({ ...s, [k]: Number(e.target.value) });
  const save = async () => {
    try { await api.put("/admin/settings", s); toast.success("Impostazioni salvate"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card title="Raccolta recensioni e garanzia">
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Nome negozio"><Input value={s.shop_name} onChange={(e) => setS({ ...s, shop_name: e.target.value })} data-testid="settings-shop-name" /></Field>
        <Field label="URL negozio"><Input value={s.shop_url} onChange={(e) => setS({ ...s, shop_url: e.target.value })} data-testid="settings-shop-url" /></Field>
        <Field label="Giorni di attesa prima dell'invito" hint="Dal momento in cui l'ordine raggiunge lo stato scelto"><Input type="number" min="0" max="60" value={s.delay_days} onChange={num("delay_days")} data-testid="settings-delay-days" /></Field>
        <Field label="Stato ordine che attiva l'invito">
          <Select value={s.trigger_status} onValueChange={(v) => setS({ ...s, trigger_status: v })}>
            <SelectTrigger data-testid="settings-trigger-status"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="completed">Completato</SelectItem><SelectItem value="processing">In lavorazione</SelectItem></SelectContent>
          </Select>
        </Field>
        <Field label="Durata garanzia (giorni dall'ordine)"><Input type="number" min="1" max="365" value={s.guarantee_days} onChange={num("guarantee_days")} data-testid="settings-guarantee-days" /></Field>
        <Field label="Massimale garanzia (€)"><Input type="number" min="1" value={s.guarantee_max_amount} onChange={num("guarantee_max_amount")} data-testid="settings-guarantee-max" /></Field>
        <label className="flex items-center gap-3 text-sm"><Switch checked={s.reminder_enabled} onCheckedChange={(v) => setS({ ...s, reminder_enabled: v })} data-testid="settings-reminder-enabled" /> Invia un promemoria se il cliente non recensisce</label>
        <Field label="Giorni dopo l'invito per il promemoria"><Input type="number" min="1" max="30" value={s.reminder_days} disabled={!s.reminder_enabled} onChange={num("reminder_days")} data-testid="settings-reminder-days" /></Field>
        <label className="sm:col-span-2 flex items-center gap-3 text-sm"><Switch checked={s.notify_admin} onCheckedChange={(v) => setS({ ...s, notify_admin: v })} data-testid="settings-notify-admin" /> Avvisami via email per ogni nuova recensione</label>
      </div>
      <Button className="mt-5 bg-emerald-900 hover:bg-emerald-800" onClick={save} data-testid="settings-save-button">Salva impostazioni</Button>
    </Card>
  );
};

const PasswordCard = () => {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const save = async () => {
    if (f.new_password !== f.confirm) return toast.error("Le password non coincidono");
    try { await api.post("/auth/change-password", f); toast.success("Password aggiornata. Le altre sessioni sono state chiuse."); setF({ current_password: "", new_password: "", confirm: "" }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card title="Cambia password">
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Attuale"><Input type="password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="pw-current" /></Field>
        <Field label="Nuova" hint="Min. 12 caratteri, maiuscole, minuscole, numeri"><Input type="password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="pw-new" /></Field>
        <Field label="Conferma"><Input type="password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} data-testid="pw-confirm" /></Field>
      </div>
      <Button className="mt-4" variant="outline" onClick={save} data-testid="pw-save-button">Aggiorna password</Button>
    </Card>
  );
};

const SecurityCard = () => {
  const { logout } = useAuth();
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const regen = async () => {
    try { const { data } = await api.post("/auth/2fa/backup-codes", { code }); setCodes(data.backup_codes); setCode(""); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const logoutAll = async () => { await api.post("/auth/logout-all"); toast.success("Tutte le sessioni chiuse"); logout(); };
  return (
    <Card title="Sicurezza account">
      <p className="text-sm text-gray-600">Verifica in due passaggi: <b className="text-emerald-800">attiva</b>. Blocco automatico dopo 5 tentativi errati, codice antibot ad ogni accesso.</p>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <Field label="Codice app (6 cifre)"><Input className="w-40" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} data-testid="backup-regen-code" /></Field>
        <Button variant="outline" onClick={regen} disabled={code.length !== 6} data-testid="backup-regen-button">Rigenera codici di emergenza</Button>
        <Button variant="outline" className="text-red-700 border-red-200 hover:bg-red-50" onClick={logoutAll} data-testid="logout-all-button">Esci da tutti i dispositivi</Button>
      </div>
      {codes && <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 font-mono text-sm bg-gray-50 border border-gray-200 rounded-md p-4" data-testid="new-backup-codes">{codes.map((c) => <span key={c}>{c}</span>)}</div>}
    </Card>
  );
};

const AuditCard = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/audit").then((r) => setItems(r.data.items)); }, []);
  return (
    <Card title="Registro attività">
      <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 text-sm" data-testid="audit-log">
        {items.map((a) => (
          <div key={a.id} className="py-2 flex justify-between gap-4"><span><b className={a.action.includes("failed") ? "text-red-700" : ""}>{a.action}</b> <span className="text-gray-500">{a.detail}</span></span><span className="text-xs text-gray-500 whitespace-nowrap">{a.ip} · {fmtDateTime(a.at)}</span></div>
        ))}
      </div>
    </Card>
  );
};

export default function Settings() {
  return (
    <div className="fade-up space-y-6 max-w-4xl">
      <PageHeader title="Impostazioni" />
      <GeneralSettings />
      <PasswordCard />
      <SecurityCard />
      <AuditCard />
    </div>
  );
}
